// frontend/src/components/Header.jsx
import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Link, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { MapPin, CheckCircle } from "lucide-react"; // ❌ Eliminado MessageCircle
import NotificationBell from "./NotificationBell";

// 👇 Mapeo de IDs a nombres de condominios
const condominiosMap = {
  1: "Gran Ville São Venâncio",
  2: "Bela Vista",
  3: "Residencial Pecan",
  4: "Condomínio Terras da Alvorada",
};

export default function Header({ selectedCondominio = "" }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [condominioLabel, setCondominioLabel] = useState("");
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");

  const getDisplayName = () => {
    if (!user) return "";
    return user.apelido || user.name || "Usuário";
  };

  useEffect(() => {
    const handleUserUpdated = (event) => {
      const updatedUser = event.detail;
      const newName = updatedUser.apelido || updatedUser.name || "Usuário";
      setNotificationMessage(`Perfil atualizado: ${newName}`);
      setShowNotification(true);

      setTimeout(() => {
        setShowNotification(false);
      }, 3000);
    };

    window.addEventListener("user-updated", handleUserUpdated);

    return () => {
      window.removeEventListener("user-updated", handleUserUpdated);
    };
  }, []);

  useEffect(() => {
    if (
      !selectedCondominio ||
      selectedCondominio === "" ||
      selectedCondominio === "todos"
    ) {
      setCondominioLabel("Todos os condomínios");
    } else {
      const nomeCondominio =
        condominiosMap[selectedCondominio] || selectedCondominio;
      setCondominioLabel(nomeCondominio);
    }
  }, [selectedCondominio]);

  const handleLogout = async () => {
    const result = await Swal.fire({
      title: "Deseja sair?",
      text: "Você será desconectado da sua conta.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#449879",
      confirmButtonText: "Sim, sair",
      cancelButtonText: "Cancelar",
      customClass: {
        popup: "text-sm rounded-xl",
      },
    });

    if (result.isConfirmed) {
      Swal.fire({
        title: "Saindo...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      setTimeout(() => {
        logout();
        Swal.fire({
          icon: "success",
          title: "Até logo!",
          text: "Você saiu da sua conta.",
          timer: 1500,
          showConfirmButton: false,
          position: "top-end",
          toast: true,
          customClass: {
            popup: "text-sm rounded-xl",
          },
        });
      }, 500);
    }
  };

  const shouldShowCondominio = () => {
    const isHome = location.pathname === "/";
    return isHome && condominioLabel;
  };

  return (
    <header className="bg-white/80 backdrop-blur-md fixed top-0 left-0 right-0 z-50 shadow-sm border-b border-gray-100/50">
      <div
        className={`fixed top-20 left-1/2 transform -translate-x-1/2 z-50 transition-all duration-500 ${
          showNotification
            ? "opacity-100 translate-y-0"
            : "opacity-0 -translate-y-10 pointer-events-none"
        }`}
      >
        <div className="bg-green-50 border-l-4 border-[#449879] rounded-r-lg shadow-lg px-4 py-3 flex items-center gap-3 animate-slide-down">
          <CheckCircle className="w-5 h-5 text-[#449879]" />
          <span className="text-sm text-gray-700 font-medium">
            {notificationMessage}
          </span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4">
        <div className="flex flex-col">
          {/* HEADER */}
          <div className="flex items-center justify-between h-14 md:h-16">
            {/* LOGO */}
            <Link to="/" className="flex items-center shrink-0">
              <img
                src={`${process.env.PUBLIC_URL}/img/logo2.png`}
                alt="Brechó Vizinho"
                className="h-20 md:h-24 w-auto object-contain"
              />
            </Link>

            {/* "Você está em" - SOLO DESKTOP */}
            {shouldShowCondominio() && (
              <div className="hidden md:flex items-center gap-1.5 flex-1 justify-center">
                <MapPin className="w-3.5 h-3.5 text-[#449879]" />
                <span className="text-xs text-gray-500 font-medium">
                  Você está em:
                </span>
                <span className="text-xs font-semibold text-[#449879] bg-green-50 px-2.5 py-0.5 rounded-full">
                  {condominioLabel}
                </span>
              </div>
            )}

            {/* Botones a la derecha */}
            <div className="flex items-center gap-2">
              {/* ===== MOBILE ===== */}
              <div className="flex md:hidden items-center gap-1.5">
                {!user ? (
                  <Link
                    to="/login"
                    className="bg-[#449879] text-white px-3 py-1.5 rounded-lg hover:bg-[#367d64] transition-colors font-medium text-xs"
                  >
                    Entrar
                  </Link>
                ) : (
                  <>
                    {/* 🔥 Campana de notificaciones */}
                    <NotificationBell />

                    {/* ❌ ELIMINADO: Link al chat */}

                    <div className="flex items-center gap-1.5">
                      <div className="w-7 h-7 rounded-full bg-[#449879] flex items-center justify-center text-white font-medium text-xs">
                        {getDisplayName().charAt(0).toUpperCase()}
                      </div>
                      <span className="text-gray-700 font-medium text-xs max-w-[70px] truncate">
                        {getDisplayName()}
                      </span>
                    </div>
                    <button
                      onClick={handleLogout}
                      className="bg-red-600 text-white px-2.5 py-1.5 rounded-lg hover:bg-red-700 transition-colors text-xs font-medium"
                    >
                      Sair
                    </button>
                  </>
                )}
              </div>

              {/* ===== DESKTOP ===== */}
              <div className="hidden md:flex items-center gap-2">
                {!user ? (
                  <Link
                    to="/login"
                    className="bg-[#449879] text-white px-4 py-1.5 rounded-lg hover:bg-[#367d64] transition-colors font-medium text-sm"
                  >
                    Entrar
                  </Link>
                ) : (
                  <>
                    {/* 🔥 Campana de notificaciones */}
                    <NotificationBell />

                    {/* ❌ ELIMINADO: Link al chat */}

                    <div className="flex items-center gap-1.5">
                      <div className="w-7 h-7 rounded-full bg-[#449879] flex items-center justify-center text-white font-medium text-xs">
                        {getDisplayName().charAt(0).toUpperCase()}
                      </div>
                      <span className="text-gray-700 font-medium text-sm">
                        Olá, {getDisplayName()}
                      </span>
                    </div>
                    <button
                      onClick={handleLogout}
                      className="bg-red-600 text-white px-3 py-1.5 rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                    >
                      Sair
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* "Você está em" - MOBILE */}
          {shouldShowCondominio() && (
            <div className="md:hidden flex justify-end w-full pb-1.5 -mt-1 px-2">
              <div className="flex items-center gap-1">
                <MapPin className="w-3 h-3 text-[#449879]" />
                <span className="text-[10px] text-gray-500">Você está em:</span>
                <span className="text-[10px] font-semibold text-[#449879] bg-green-50 px-2 py-0.5 rounded-full">
                  {condominioLabel}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>
        {`
          @keyframes slideDown {
            from {
              opacity: 0;
              transform: translate(-50%, -20px);
            }
            to {
              opacity: 1;
              transform: translate(-50%, 0);
            }
          }
          .animate-slide-down {
            animation: slideDown 0.3s ease-out;
          }
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.1); }
          }
          .animate-pulse {
            animation: pulse 1.5s ease-in-out infinite;
          }
        `}
      </style>
    </header>
  );
}